import { Button } from "@pickit/pickit-components";
import { useTranslation } from "react-i18next";
import FilterModal from "./filterModal/FilterModal";
import { useCallback, useEffect, useRef, useState } from "react";
import { SimpleFilterProps } from "./types";
import { DashboardFilterType } from "reducers/types/dashboard";
import { ConnectedProps, connect } from "react-redux";
import { AppDispatch, RootState } from "store";
import { actions } from "reducers/dashboard";
import { sessionSelector } from "reducers/session";

const SimpleFilter = ({
  regions,
  value,
  fetching,
  testId,
  getDashboard,
  setDashboardFilters,
}: SimpleFilterProps & ConnectorProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const valueRef = useRef<string | undefined>(value || "");
  const previousValueRef = useRef<string | undefined>(value || "");

  const search = useCallback(
    (values: DashboardFilterType) => {
      if (previousValueRef.current !== values.regions) {
        previousValueRef.current = values.regions;
        setDashboardFilters(values);
        getDashboard(values);
      }
    },
    [getDashboard, setDashboardFilters]
  );

  useEffect(() => {
    previousValueRef.current = value;
  }, [value]);

  const globalClose = useCallback(
    (e) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !fetching
      ) {
        search({
          regions: valueRef.current || regions.map((r) => r.id).join(","),
        });
        setIsModalOpen(false);
      }
    },
    [fetching, regions, search]
  );

  useEffect(() => {
    if (isModalOpen) {
      window.addEventListener("mousedown", globalClose);
    } else {
      window.removeEventListener("mousedown", globalClose);
    }
  }, [globalClose, isModalOpen]);

  return (
    <>
      <div ref={buttonRef}>
        <Button
          label={t("dashboard:changeRegions")}
          hierarchy="minimal"
          size="small"
          onClick={() => setIsModalOpen(true)}
          testId={`${testId}-button`}
        />
      </div>
      <FilterModal
        isOpen={isModalOpen}
        parentRef={buttonRef}
        modalRef={modalRef}
        regions={regions}
        initialValue={value}
        valueRef={valueRef}
        testId={`${testId}-selector`}
        fetching={fetching}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  fetching: sessionSelector(state).fetching,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setDashboardFilters: (filters: DashboardFilterType) => {
    dispatch(actions.setDashboardFilters(filters));
  },
  getDashboard: (params: DashboardFilterType) => {
    dispatch(actions.getDashboard(params));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(SimpleFilter);
